// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Img, { ImgProps } from "@insite/mobius/Img";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useState } from "react";
import { css } from "styled-components";

const enum fields {
    icon = "icon",
    iconOnHover = "iconOnHover",
    title = "title",
    contentText = "contentText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.icon]: string;
        [fields.iconOnHover]: string;
        [fields.title]: string;
        [fields.contentText]: string;
    };
    extendedStyles?: ContentBoxStyles;
}

export interface ContentBoxStyles {
    wrapper?: InjectableCss;
    wrapperOnHover?: InjectableCss;
    icon?: ImgProps;
    iconOnHover?: ImgProps;
    titleWrapper?: InjectableCss;
    titleWrapperOnHover?: InjectableCss;
    title?: TypographyPresentationProps;
    titleOnHover?: TypographyPresentationProps;
    contentTextWrapper?: InjectableCss;
    contentTextWrapperOnHover?: InjectableCss;
    contentText?: TypographyPresentationProps;
}

export const contentBoxStyles: ContentBoxStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background-color: ${getColor("background.alt")};
            transition: all 0.5s;
            & * {
                transition: all 0.5s;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: ${pxToRem(470)};
                        `,
                        css`
                            height: ${pxToRem(400)};
                        `,
                        null,
                        null,
                        css`
                            height: ${pxToRem(340)};
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    wrapperOnHover: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background-color: ${getColor("primary")};
            transition: all 0.5s;
            & * {
                transition: all 0.5s;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: ${pxToRem(470)};
                        `,
                        css`
                            height: ${pxToRem(400)};
                        `,
                        null,
                        null,
                        css`
                            height: ${pxToRem(340)};
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    icon: {
        height: `${pxToRem(165)}`,
        width: `${pxToRem(165)}`,
    },
    iconOnHover: {
        height: `${pxToRem(165)}`,
        width: `${pxToRem(165)}`,
        css: css`
            div {
                transform: scale(0.6);
            }
            transform: scale(0.6);
        `,
    },
    titleWrapper: {
        css: css`
            border-top: ${pxToRem(3)} solid ${getColor("primary")};
            margin-top: ${pxToRem(15)};
            padding: ${pxToRem(10, 0)};
        `,
    },
    titleWrapperOnHover: {
        css: css`
            border-top: ${pxToRem(3)} solid ${getColor("white")};
            margin-top: -${pxToRem(20)};
            padding: ${pxToRem(10, 0)};
        `,
    },
    title: {
        transform: "uppercase",
        variant: "h3",
        css: css`
            font-size: ${pxToRem(24)} !important;
        `,
    },
    titleOnHover: {
        transform: "uppercase",
        variant: "h3",
        color: "white",
        css: css`
            font-size: ${pxToRem(18)} !important;
        `,
    },
    contentTextWrapper: {
        css: css`
            opacity: 0;
            height: 0;
        `,
    },
    contentTextWrapperOnHover: {
        css: css`
            opacity: 1;
            height: 50%;
        `,
    },
    contentText: {
        variant: "p",
        color: "white",
        size: 12,
        css: css`
            text-align: center;
            padding: ${pxToRem(0, 28)};
        `,
    },
};
const styles = contentBoxStyles;

const ContentBox = ({ fields }: OwnProps) => {
    const [hoverState, setHoverstate] = useState(false);

    return (
        <StyledWrapper
            onMouseOver={() => setHoverstate(true)}
            onMouseOut={() => setHoverstate(false)}
            {...(hoverState ? styles.wrapperOnHover : styles.wrapper)}
        >
            <Img
                {...(hoverState ? styles.iconOnHover : styles.icon)}
                src={hoverState ? fields.iconOnHover : fields.icon}
                altText={translate("Box Icon")}
                loading="eager"
            />
            <StyledWrapper {...(hoverState ? styles.titleWrapperOnHover : styles.titleWrapper)}>
                <Typography {...(hoverState ? styles.titleOnHover : styles.title)}>{fields.title}</Typography>
            </StyledWrapper>
            <StyledWrapper {...(hoverState ? styles.contentTextWrapperOnHover : styles.contentTextWrapper)}>
                <Typography {...styles.contentText}>{fields.contentText}</Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ContentBox,
    definition: {
        group: "Basic",
        icon: "square-plus",
        fieldDefinitions: [
            {
                name: fields.icon,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
            {
                name: fields.iconOnHover,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
                defaultValue: "",
            },
            {
                name: fields.contentText,
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
